import * as R from 'ramda'
import * as React from 'react'
import { useMemo } from 'react'

import { APP_SERVICES } from '@ngb-frontend/shared/config'
import { useBusinessRules, useQuery } from '@ngb-frontend/shared/context'
import {
  Feature,
  type ServiceFormValues,
  type ServiceStepComponentProps,
  type ServiceStepPrerequisiteData,
  type ServiceValue,
  type StepProps,
} from '@ngb-frontend/shared/types'
import {
  camelize,
  getServiceStepDataFromBooking,
  isLPSystem,
  useVehicle,
} from '@ngb-frontend/shared/utils'

import { ServiceForm } from '../../forms/ServiceForm/ServiceForm'

const ServiceStep: React.FC<
  StepProps<
    ServiceFormValues,
    ServiceStepPrerequisiteData,
    ServiceStepComponentProps
  >
> = ({ onNextStep, onUpdateStep, stepData, variant, booking, mergedData }) => {
  const variantKey = camelize(variant) as keyof ServiceFormValues
  const rules = useBusinessRules()

  const initialValuesRef = React.useRef<ServiceFormValues | undefined>(
    hasServiceFormValues(stepData, variantKey)
      ? /**
         * To make typing interactions with the form easier, from this point and
         * until data have to be written in persistence we call all variants
         * of services 'services'.
         */
        { services: stepData[variantKey] }
      : booking
      ? getServiceStepDataFromBooking(booking, variantKey)
      : { services: [] },
  )
  const query = useQuery()

  const { data: vehicle } = useVehicle(query)

  const handleSubmit = React.useCallback(
    (values: ServiceFormValues) => {
      onUpdateStep(values)
      onNextStep()
    },
    [onNextStep, onUpdateStep],
  )

  const isLP = isLPSystem(query?.systemCode)

  // Which main services are offered is independent of the current selection;
  // the live MOT ↔ main-service exclusion is applied in ServiceForm
  const displayedServices = useMemo(
    () =>
      variantKey === 'extraServices'
        ? APP_SERVICES[variant]
        : rules.services({
            selection: {},
            journey: Feature.MnR,
            usesVehicleCatalog: isLP,
            vehicleMainServices: vehicle?.services,
          }).main.displayed,
    [rules, variant, variantKey, isLP, vehicle?.services],
  )

  const enabledServices = useMemo(() => {
    if (variantKey === 'extraServices') {
      // Extra availability depends on the committed main services, not the live
      // extra selection
      const committedMainServices = (
        mergedData as { services?: ServiceValue[] }
      ).services
      return rules.services({
        selection: { services: committedMainServices },
        journey: Feature.MnR,
        usesVehicleCatalog: isLP,
      }).extra.enabled
    }

    return rules.services({
      selection: {},
      journey: Feature.MnR,
      usesVehicleCatalog: isLP,
      vehicleMainServices: vehicle?.services,
    }).main.enabled
  }, [rules, variantKey, isLP, mergedData, vehicle?.services])

  return (
    <ServiceForm
      variant={variant}
      onSubmit={handleSubmit}
      initialValues={initialValuesRef.current}
      displayedServices={displayedServices}
      enabledServices={enabledServices}
    />
  )
}

export const hasServiceFormValues = (
  x: unknown,
  variantDataKey: string,
): x is ServiceFormValues =>
  R.allPass([R.pipe(R.prop(variantDataKey), Array.isArray)])(x)

export default ServiceStep
